import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Medal, Award, Sparkles, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const PERIODS = [
  { key: "current_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "last_30_days", label: "Last 30 Days" },
];

const PODIUM = [
  { Icon: Trophy, ring: "ring-amber-400", chip: "bg-amber-100 text-amber-800 border-amber-300", label: "Gold" },
  { Icon: Medal, ring: "ring-slate-300", chip: "bg-slate-100 text-slate-700 border-slate-300", label: "Silver" },
  { Icon: Award, ring: "ring-orange-400", chip: "bg-orange-100 text-orange-800 border-orange-300", label: "Bronze" },
];

export default function StarPerformer({ compact = false }) {
  const [period, setPeriod] = useState("current_month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (p = period) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/commission/leaderboard?period=${p}&top=3`);
      setData(res.data);
    } catch (e) {
      // Silent — this is a nice-to-have widget on the till
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(period); /* eslint-disable-next-line */ }, [period]);

  const top = data?.top || [];
  const hasData = top.length > 0;

  return (
    <div
      className={`bg-gradient-to-br from-indigo-50 via-amber-50 to-rose-50 border-2 border-amber-200 rounded-xl shadow-md ${compact ? "p-3" : "p-5"} max-w-2xl mx-auto`}
      data-testid="star-performer-widget"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-500" />
          <h3 className={`${compact ? "text-base" : "text-lg"} font-bold text-slate-800`}>Star Performers</h3>
          <span className="text-xs text-slate-500">· {data?.period_label || "—"}</span>
        </div>
        <div className="flex items-center gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`text-[11px] px-2 py-1 rounded-full border transition-all ${
                period === p.key
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => load()}
            className="ml-1 p-1 rounded text-slate-400 hover:text-slate-700"
            title="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {!hasData && !loading && (
        <div className="text-center py-6 text-sm text-slate-500" data-testid="star-empty">
          No salesperson sales recorded yet for this period. Pick a salesperson at the till to start tracking!
        </div>
      )}

      {hasData && (
        <div className={`grid ${compact ? "grid-cols-3 gap-2" : "grid-cols-3 gap-3"} items-end`}>
          {/* Render in podium order: silver, gold, bronze (visually centred). */}
          {[1, 0, 2].map((idx) => {
            const r = top[idx];
            if (!r) return <div key={`empty-${idx}`} />;
            const def = PODIUM[idx];
            const isGold = idx === 0;
            return (
              <div
                key={r.salesperson || r.marketing_number || idx}
                className={`bg-white rounded-lg p-3 text-center ring-2 ${def.ring} ${isGold ? "scale-105 shadow-lg" : "shadow"} ${compact ? "" : "py-4"}`}
                data-testid={`podium-${idx + 1}`}
              >
                <div className={`mx-auto h-9 w-9 rounded-full ${def.chip} border flex items-center justify-center mb-2`}>
                  <def.Icon className="h-5 w-5" />
                </div>
                <div className={`font-bold ${isGold ? "text-base" : "text-sm"} text-slate-800 truncate`} title={r.salesperson}>
                  {r.salesperson}
                  {r.marketing_number && <span className="ml-1 text-[10px] text-slate-400 font-mono">#{r.marketing_number}</span>}
                </div>
                <div className={`${isGold ? "text-amber-700 font-bold text-lg" : "text-slate-700 font-semibold"} mt-1`}>
                  {fmt(r.gross_profit)} <span className="text-[10px] text-slate-500 font-normal">GP</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {r.transactions} sales
                  {r.behaviour_events > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px]">+{r.behaviour_events} ⭐</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
